import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Search, Filter, LayoutTemplate, Sparkles } from "lucide-react";
import { INDUSTRIES, TEMPLATE_CATEGORIES, type WorkflowTemplate } from "@/lib/template-constants";

// ────────────────────────────────────────────────────────────
// Template Browser Dialog
// ────────────────────────────────────────────────────────────

export function TemplateBrowserDialog({
  open,
  onClose,
  onSelectTemplate,
  templateType,
  title = "Choose a Template",
  description = "Browse pre-built templates and customize them with AI",
}: {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: WorkflowTemplate) => void;
  templateType: "workflow" | "dashboard" | "report" | "research";
  title?: string;
  description?: string;
}) {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndustries, setSelectedIndustries] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    supabase
      .from("workflow_template" as any)
      .select("*")
      .eq("is_active", true)
      .eq("template_type", templateType)
      .order("name")
      .then(({ data }) => {
        setTemplates((data as unknown as WorkflowTemplate[]) ?? []);
        setLoading(false);
      });
  }, [open, templateType]);

  const toggleIndustry = (ind: string) => {
    setSelectedIndustries((prev) => {
      const next = new Set(prev);
      if (next.has(ind)) next.delete(ind);
      else next.add(ind);
      return next;
    });
  };

  const matchesIndustry = (t: WorkflowTemplate) =>
    selectedIndustries.size === 0 || t.industries.includes("all") || t.industries.some((i) => selectedIndustries.has(i));

  const matchesCategory = (t: WorkflowTemplate, cat: string) =>
    t.categories.includes("all") || t.categories.includes(cat);

  const filtered = templates.filter((t) => {
    if (!matchesIndustry(t)) return false;
    if (selectedCategory && !matchesCategory(t, selectedCategory)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return t.name.toLowerCase().includes(q) || (t.description ?? "").toLowerCase().includes(q);
    }
    return true;
  });

  const categoryCounts = TEMPLATE_CATEGORIES.map((cat) => {
    const count = templates.filter((t) => {
      if (!matchesIndustry(t)) return false;
      return matchesCategory(t, cat);
    }).length;
    return { category: cat, count };
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="text-lg">{title}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {description}
          </DialogDescription>
          {/* Industry filter chips */}
          <div className="flex flex-wrap gap-1.5 pt-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mr-1">
              <Filter className="h-3.5 w-3.5" />
              Industry:
            </div>
            {INDUSTRIES.map((ind) => (
              <button
                key={ind}
                onClick={() => toggleIndustry(ind)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedIndustries.has(ind)
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {ind}
              </button>
            ))}
            {selectedIndustries.size > 0 && (
              <button
                onClick={() => setSelectedIndustries(new Set())}
                className="px-2 py-1 rounded-full text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Category sidebar */}
          <div className="w-52 border-r shrink-0 overflow-y-auto">
            <div className="p-3 space-y-0.5">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  !selectedCategory ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                All Categories
                <span className="ml-1.5 text-xs opacity-60">({filtered.length})</span>
              </button>
              {categoryCounts.map(({ category, count }) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedCategory === category
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {category}
                  <span className="ml-1.5 text-xs opacity-60">({count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Template cards */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Search */}
            <div className="px-4 py-3 border-b shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <LayoutTemplate className="h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">No templates match your filters</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {filtered.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => onSelectTemplate(t)}
                        className="text-left rounded-lg border border-border p-4 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
                      >
                        <p className="text-sm font-semibold group-hover:text-primary transition-colors">{t.name}</p>
                        {t.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-3">
                          {t.categories.map((c) => (
                            <Badge key={c} variant="secondary" className="text-[10px] px-1.5 py-0">
                              {c}
                            </Badge>
                          ))}
                          {t.industries.slice(0, 3).map((i) => (
                            <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                              {i}
                            </Badge>
                          ))}
                          {t.industries.length > 3 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              +{t.industries.length - 3}
                            </Badge>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────
// Template Prompt Dialog
// ────────────────────────────────────────────────────────────

export function TemplatePromptDialog({
  open,
  onClose,
  template,
  onGenerate,
  generateLabel = "Generate with AI",
  placeholder = "Describe what you want to build...",
}: {
  open: boolean;
  onClose: () => void;
  template: WorkflowTemplate | null;
  onGenerate: (prompt: string) => void;
  generateLabel?: string;
  placeholder?: string;
}) {
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    if (template) setPrompt(template.prompt_message);
  }, [template]);

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>
          {template.description && (
            <DialogDescription>{template.description}</DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex flex-wrap gap-1">
            {template.categories.map((c) => (
              <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
            ))}
            {template.industries.map((i) => (
              <Badge key={i} variant="outline" className="text-xs">{i}</Badge>
            ))}
          </div>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={8}
            className="text-sm resize-none"
            placeholder={placeholder}
          />
          <p className="text-xs text-muted-foreground">
            Edit the prompt above to customize it for your use case, then generate.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onGenerate(prompt)}
            disabled={!prompt.trim()}
            className="gap-2 bg-violet-600 hover:bg-violet-700 text-white"
          >
            <Sparkles className="h-4 w-4" />
            {generateLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
